import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { useAuthStore } from '@/store/auth.store';
import { User } from '@/types';
import { useState } from 'react';

interface LoginResponse {
  mfaRequired?: boolean;
  accessToken:  string;
  refreshToken?: string; // still returned by server but no longer stored client-side
  user: User;
  clientIp?: string;
  /** The policy wants a second factor this account does not have yet. */
  mfaSetupRequired?: boolean;
}

export function useAuth() {
  const navigate  = useNavigate();
  const { setAuth, logout: clearAuth, user, token } = useAuthStore();
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCreds, setMfaCreds] = useState<{ email: string; password: string } | null>(null);

  const loginMutation = useMutation({
    mutationFn: async (creds: { email: string; password: string; mfaCode?: string }) => {
      const { data } = await api.post<{ data: LoginResponse }>('/auth/login', creds);
      return data.data;
    },
    onSuccess: (data) => {
      if (data.mfaRequired) {
        setMfaRequired(true);
        return;
      }
      setMfaRequired(false);
      setMfaCreds(null);
      setAuth(data.accessToken, data.user, data.clientIp);
      navigate(data.mfaSetupRequired ? '/profile?mfa=required' : '/dashboard');
    },
  });

  const login = (creds: { email: string; password: string }) => {
    setMfaCreds(creds);
    loginMutation.mutate(creds);
  };

  const verifyMfa = (mfaCode: string) => {
    if (!mfaCreds) return;
    loginMutation.mutate({ ...mfaCreds, mfaCode });
  };

  const logoutMutation = useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSettled: () => {
      clearAuth();
      navigate('/login');
    },
  });

  return {
    user,
    isAuthenticated: !!token,
    login,
    verifyMfa,
    mfaRequired,
    logout: logoutMutation.mutate,
    loginError:   loginMutation.error,
    loginPending: loginMutation.isPending,
  };
}
